import { createContext, useCallback, useContext, useState } from 'react';
import {
  GrcAccordion,
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType
} from '@grc/ui-package';
import PatternEditor from './pattern-editor';
import { canonicalizeTemplate } from './template-parser';
import {
  DEFAULT_SEED,
  IDENTITY_EXAMPLES,
  IDENTITY_LABELS,
  IDENTITY_PATTERN_KEYS,
  IDENTITY_SEED_KEYS,
  IdentityPatterns,
  IdentitySeedKey
} from './tenant-types';

interface IdentityItem {
  key: IdentitySeedKey;
  title: string;
}

/**
 * Built once and never rebuilt. DevExtreme re-creates every item template when the
 * dataSource identity changes, which would drop focus out of the editor mid-keystroke,
 * so the live values travel by context instead (below) and this array stays constant.
 */
const IDENTITY_ITEMS: IdentityItem[] = IDENTITY_SEED_KEYS.map(key => ({
  key,
  title: IDENTITY_LABELS[key]
}));

export interface IdentityPatternsState {
  seeds: Record<IdentitySeedKey, number>;
  templates: Record<IdentitySeedKey, string>;
  setSeed: (key: IdentitySeedKey, value: number) => void;
  setTemplate: (key: IdentitySeedKey, next: string) => void;
  reset: () => void;
}

const IdentityPatternsContext = createContext<IdentityPatternsState | null>(null);

function defaultSeeds(): Record<IdentitySeedKey, number> {
  return IDENTITY_SEED_KEYS.reduce(
    (acc, k) => {
      acc[k] = DEFAULT_SEED;
      return acc;
    },
    {} as Record<IdentitySeedKey, number>
  );
}

function defaultTemplates(): Record<IdentitySeedKey, string> {
  return IDENTITY_SEED_KEYS.reduce(
    (acc, k) => {
      acc[k] = `${k.charAt(0)}{ID}`;
      return acc;
    },
    {} as Record<IdentitySeedKey, string>
  );
}

export function useIdentityPatterns(): IdentityPatternsState {
  const [seeds, setSeeds] = useState<Record<IdentitySeedKey, number>>(defaultSeeds);
  const [templates, setTemplates] = useState<Record<IdentitySeedKey, string>>(defaultTemplates);

  const setSeed = useCallback(
    (key: IdentitySeedKey, value: number) => setSeeds(prev => ({ ...prev, [key]: value })),
    []
  );

  const setTemplate = useCallback(
    (key: IdentitySeedKey, next: string) => setTemplates(prev => ({ ...prev, [key]: next })),
    []
  );

  const reset = useCallback(() => {
    setSeeds(defaultSeeds());
    setTemplates(defaultTemplates());
  }, []);

  return { seeds, templates, setSeed, setTemplate, reset };
}

/** Shapes the template map into the request body's pattern field names. */
export function toIdentityPatterns(templates: Record<IdentitySeedKey, string>): IdentityPatterns {
  return IDENTITY_SEED_KEYS.reduce<IdentityPatterns>((acc, k) => {
    (acc as Record<string, string>)[IDENTITY_PATTERN_KEYS[k]] = templates[k];
    return acc;
  }, {});
}

/**
 * One accordion panel. DevExtreme renders item templates as portals back into this
 * React tree, so the panel reads live seeds and templates from context rather than
 * from the (deliberately frozen) item data.
 */
function IdentityPanel({ data }: { data: IdentityItem }) {
  const patterns = useContext(IdentityPatternsContext);
  if (!patterns) return null;

  const { key } = data;

  return (
    <div className="identity-panel">
      <PatternEditor
        idPrefix={`pattern-${key}`}
        seed={patterns.seeds[key]}
        onSeedChange={value => patterns.setSeed(key, value)}
        template={patterns.templates[key]}
        onTemplateChange={next => patterns.setTemplate(key, next)}
      />

      {/*
        The examples live inside the panel they apply to, so "use this one" needs no
        explanation: there is only one identity on screen to use it in.
      */}
      <div className="pattern-examples">
        <span className="pattern-examples-label">Start from an example</span>
        {IDENTITY_EXAMPLES.map(example => (
          <div className="pattern-example" key={example.name}>
            <GrcButton
              id={`identity-example-${key}-${example.name.replace(/\s+/g, '-')}`}
              text={example.name}
              type={GrcButtonType.Default}
              stylingMode={GrcButtonStylingMode.Text}
              onClick={() => patterns.setTemplate(key, canonicalizeTemplate(example.template))}
            />
            <code className="pattern-example-template">{example.template}</code>
            <span className="pattern-example-desc">{example.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Exactly one identity is expanded at a time and it cannot be collapsed to none —
 * the open panel is the "which identity am I editing" state, shown by the component
 * rather than by a pill and a border colour.
 */
export default function IdentityAccordion({ state }: { state: IdentityPatternsState }) {
  return (
    <IdentityPatternsContext.Provider value={state}>
      <GrcAccordion
        id="tenant-identities"
        dataSource={IDENTITY_ITEMS}
        itemKeyFn={(item: IdentityItem) => item.key}
        itemComponent={IdentityPanel}
        collapsible={false}
        multiple={false}
      />
    </IdentityPatternsContext.Provider>
  );
}
